import { useEffect, useState } from 'react';
import { MAX_HANDICAP, MIN_HANDICAP, type Handicap } from '@aiscoreboard/contracts';
import { Toggle } from './Toggle';
import { accentOf } from '../lib/accent';
import { useT } from '../lib/i18n';

/** A start most clubs would recognise: a frame's worth of a decent break. */
const DEFAULT_POINTS = 20;
/** One click is worth five points, the granularity a handicap is agreed in. */
const STEP = 5;

const clamp = (n: number): number => Math.min(MAX_HANDICAP, Math.max(MIN_HANDICAP, n));

/**
 * The start one player gives the other.
 *
 * Two fields rather than one field and a swap: which of the two is the
 * stronger player is the first thing being decided, and a control that makes
 * you set a number *and then* say who it belongs to invites the number being
 * given to the wrong man. Only one of them can be giving at a time — a mutual
 * handicap is two numbers that cancel — so typing into one clears the other.
 *
 * Nought in a field means that player gives nothing, which is why the switch
 * exists as well: with it off there is no handicap on the match at all, and
 * the rows stay on screen greyed out so the panel keeps its height.
 */
export function HandicapField({
  value,
  onChange,
}: {
  value: Handicap | null;
  onChange: (handicap: Handicap | null) => void;
}) {
  const t = useT();
  // Held locally, because both fields at nought is a legitimate state of a
  // switch that is on — it means nobody is giving anything yet — and it is
  // reported upwards as `null`, which is also what the switch being off is.
  const [enabled, setEnabled] = useState(value !== null);

  useEffect(() => {
    if (value !== null) setEnabled(true);
  }, [value]);

  const pointsFor = (seat: 0 | 1): number =>
    value && value.from === seat ? value.points : 0;

  const set = (seat: 0 | 1, points: number): void => {
    onChange(points <= 0 ? null : { from: seat, points: clamp(points) });
  };

  return (
    // Spread down whatever height the panel gives it rather than sitting as a
    // block in the middle: this is the one control in the column with two
    // parts to it, and the column's spare room is there to separate them.
    <div className="flex w-full flex-1 flex-col items-center justify-evenly gap-1.5">
      <div className="flex w-full items-center justify-center gap-2">
        <span className={`label mb-0 text-center ${enabled ? '' : 'text-white/30'}`}>
          {t('handicap.toggle')}
        </span>
        <Toggle
          checked={enabled}
          label={t('handicap.toggle')}
          onChange={(on) => {
            setEnabled(on);
            onChange(on ? { from: 0, points: DEFAULT_POINTS } : null);
          }}
        />
      </div>

      <div className={`flex w-full flex-col gap-1 ${enabled ? '' : 'opacity-30'}`}>
        {([0, 1] as const).map((seat) => (
          <GiveRow
            key={seat}
            seat={seat}
            points={pointsFor(seat)}
            enabled={enabled}
            onSet={(points) => set(seat, points)}
          />
        ))}
      </div>

    </div>
  );
}

/** One player's line: who gives, how much, and the two ways to change it. */
function GiveRow({
  seat,
  points,
  enabled,
  onSet,
}: {
  seat: 0 | 1;
  points: number;
  enabled: boolean;
  onSet: (points: number) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState(String(points));

  useEffect(() => {
    setDraft(String(points));
  }, [points]);

  const commit = (raw: string): void => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(points));
      return;
    }
    onSet(parsed <= 0 ? 0 : clamp(parsed));
  };

  // From nought the first click gives a step, not one point: a handicap of a
  // single point is a rounding error, and nobody wants four taps to reach it.
  const step = (by: number): void => {
    if (points === 0) {
      onSet(by > 0 ? STEP : 0);
      return;
    }
    const next = points + by;
    onSet(next < MIN_HANDICAP ? 0 : clamp(next));
  };

  const label = t('handicap.gives', { name: t('seat.player', { n: seat + 1 }) });
  const accent = accentOf(seat);

  return (
    <div className="flex w-full items-center justify-center gap-1.5">
      {/* The seat's number in the seat's colour, not its name: "Spieler 2
          gibt" does not fit beside a stepper in a 15rem column in any
          language, and a label that has to be cut in half to fit is worse
          than one that was never there. The full sentence is read out to
          assistive technology through the control labels. */}
      <span
        aria-hidden
        className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-black"
        style={{ backgroundColor: `rgba(${accent.rgb},0.22)`, color: accent.hex }}
      >
        {seat + 1}
      </span>
      <span className="shrink-0 text-xs text-white/55">{t('handicap.givesShort')}</span>
      <button
        type="button"
        onClick={() => step(-STEP)}
        disabled={!enabled || points === 0}
        aria-label={`${label} — ${t('handicap.less')}`}
        className="step-btn h-8 w-8 text-base"
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={label}
        disabled={!enabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className="field w-12 shrink-0 px-1 py-1 text-center text-lg num-lg"
      />
      <button
        type="button"
        onClick={() => step(STEP)}
        disabled={!enabled || points >= MAX_HANDICAP}
        aria-label={`${label} — ${t('handicap.more')}`}
        className="step-btn h-8 w-8 text-base"
      >
        +
      </button>
    </div>
  );
}
